import { View, Text, ScrollView, ActivityIndicator, NativeSyntheticEvent, NativeScrollEvent } from 'react-native';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import i18n from 'i18n-js';
import { StackNavigationProp } from '@react-navigation/stack';

import { getMachinesOperations } from '@reducers/machines';
import Loading from '@components/loading';
import { bottomBarHeight, fullHeight, height, notchBottom, scale } from '@components/frame';
import Svg from '@styles/icons/svg';
import { font } from '@styles/fonts';
import { SCREEN_BORDER_RADIUS, SCREEN_PADDING_HORIZONTAL } from '@v2/pages/common/constants/base.constants.ts';
import { isPhone, isPhoneNative, isWeb } from '@helpers';
import { getBoxShadowStyle } from '@styles/shadow';
import Line from '@components/common/line';
import AnimatedTable from '@components/settings/components/common/animatedTable';
import { Operation } from '@interfaces/machines';
import { ColorFunction } from '@styles/colors/types.ts';
import { GetMachinesOperations } from '@interfaces/methods';

const PAGE_SIZE = 75;

interface MachinesOperationsProps {
  C: ColorFunction;
  getMachinesOperations: GetMachinesOperations;
  machinesOperationsLoaded: boolean;
  navigation: StackNavigationProp<any>;
  machinesOperations: any;
}

const MachinesOperations = ({ C, getMachinesOperations, machinesOperationsLoaded, navigation, machinesOperations }: MachinesOperationsProps) => {
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    getMachinesOperations({ filter: 'outsourced eq false', orderBy: 'operationName asc' });
  }, []);

  if (!machinesOperationsLoaded && !machinesOperations.length) {
    return (
      <View style={{ width: '100%', height: '70%', justifyContent: 'center', alignContent: 'center', alignItems: 'center' }}>
        <Loading background="transparent" />
      </View>
    );
  }

  if (!machinesOperations.length) {
    const fontStyles = font.Body;
    return (
      <View
        style={{
          position: 'absolute',
          top: isWeb || isPhone ? 0 : -bottomBarHeight - notchBottom,
          left: 0,
          width: '100%',
          height: isWeb ? height : fullHeight,
          justifyContent: 'center',
          alignItems: 'center',
          paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL),
        }}
      >
        <View style={{ alignItems: 'center', gap: scale(fontStyles.lineHeight), justifyContent: 'space-between' }}>
          <Text style={[fontStyles, { color: C('Label/Light/Tertiary') }]}>{i18n.t('No Operations Yet')}</Text>
          <Text style={[fontStyles, { color: C('Label/Light/Tertiary') }, isPhoneNative && { textAlign: 'center' }]}>
            {i18n.t('Operations from your ERP system will automatically show up here and can be assigned to machines.')}
          </Text>
        </View>
      </View>
    );
  }

  const openOperation = (i: Operation) => {
    navigation.navigate('Settings', {
      screen: 'Machines',
      params: {
        screen: 'MachinesOperationDetails',
        params: {
          id: i.operationId,
        },
      },
    });
  };

  const machinesOperationsFormatted: Operation[] = machinesOperations.map((i: Operation) => {
    const machinesList = i?.machines?.length ? i.machines.map((i) => i.shortName).join(', ') : '-';
    return {
      ...i,
      bottom: machinesList,
      machinesList,
      name: i.operationName,
      onClick: () => {
        openOperation(i);
      },
    };
  });

  const ArrowComponent = () => {
    return (
      <View style={{ marginLeft: scale(5) }}>
        <Svg icon="chevronRightLight" color="Label/Light/Quinary" width={scale(7)} height={scale(12)} />
      </View>
    );
  };

  const dataToShow = machinesOperationsFormatted
    .slice(0, page * PAGE_SIZE)
    .map((i) => ({ ...i, operationName: `${i.operationNumber ? i.operationNumber + ' ' : ''}${i.operationName}` }));
  const hasMore = dataToShow.length < machinesOperationsFormatted.length;

  const handleScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { layoutMeasurement, contentOffset, contentSize } = e.nativeEvent;
    const isBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height - 800;
    const shouldLoadMore = hasMore && !loadingMore;

    if (isBottom && shouldLoadMore) {
      setLoadingMore(true);
      setTimeout(() => {
        setPage((p) => p + 1);
        setLoadingMore(false);
      }, 150);
    }
  };

  return (
    <View style={{ width: '100%', flex: 1 }}>
      <ScrollView
        style={{ width: '100%', flex: 1 }}
        contentContainerStyle={{ paddingBottom: scale(50), paddingTop: scale(2) }}
        onScroll={handleScroll}
        scrollEventThrottle={250}
      >
        <View
          style={{
            paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL),
          }}
        >
          {isPhone ? (
            <View
              style={[
                getBoxShadowStyle({ type: 'Field', C }),
                {
                  borderRadius: scale(SCREEN_BORDER_RADIUS),
                },
              ]}
            >
              {dataToShow.map((i, index: number) => (
                <Line
                  key={index}
                  {...{
                    item: i,
                    index,
                    array: dataToShow,
                    bigSize: true,
                    C,
                    topBorder: index !== 0,
                    roundTop: index === 0,
                    roundBottom: index === dataToShow.length - 1,
                  }}
                />
              ))}
            </View>
          ) : (
            <AnimatedTable
              {...{
                C,
                loaded: machinesOperationsLoaded,
                array: dataToShow,
                headerSize: 50,
                lineSize: 63,
                showMoreSize: 63,
                onPress: openOperation,
                config: [
                  {
                    key: 'operationName',
                    title: 'Operation',
                    width: '40%',
                    color: C('Label / Light / Secondary'),
                    titleColor: C('Label / Light / Secondary'),
                  },
                  {
                    key: 'machinesList',
                    title: 'Machines',
                    width: '60%',
                    color: C('Label / Light / Secondary'),
                    titleColor: C('Label / Light / Secondary'),
                  },
                  {
                    function: ArrowComponent,
                    title: '',
                  },
                ],
              }}
            />
          )}
          {loadingMore && (
            <View style={{ padding: scale(16), alignItems: 'center', justifyContent: 'center' }}>
              <ActivityIndicator size="small" color="#888" />
            </View>
          )}
        </View>
      </ScrollView>
    </View>
  );
};

export default connect(
  ({ machines: { machinesOperations, machinesOperationsLoaded } }) => {
    return { machinesOperations, machinesOperationsLoaded };
  },
  {
    getMachinesOperations,
  }
)(MachinesOperations);
